// global-agent.js — the GLOBAL command-agent brain: a page-aware, run-as-user AI
// available on EVERY staff screen (both the OPD and the IPD/ward worlds). It lets
// a logged-in staff member ask, in voice or text, to do anything they could do by
// clicking — bounded to exactly what their Medplum AccessPolicy (RLS) allows.
//
// WHY THIS EXISTS ────────────────────────────────────────────────────────────
// The consult screen has its own CommandAgent (draft-editing), and the ward bed
// has its command-dock. This is the EVERYWHERE agent: navigate the app, open a
// patient, apply a protocol, create a task, send a message, book an appointment,
// star a favourite — the app-wide verbs, not a single screen's. It is built on the
// SAME native-`$ai` agent loop (medplum-ai.js runAgentLoop) that Spaces uses, so
// every read/write runs AS THE LOGGED-IN USER through their own MedplumClient (RLS
// the only lock — AGENTS.md §2 owner override). Nothing here is behind the
// chart-read gate; nothing runs with a service account.
//
// PROPOSE-ONLY BY CONSTRUCTION ────────────────────────────────────────────────
// The model can READ (fhir_request GET) but can only PROPOSE writes/navigations via
// propose_actions. Every proposed action is validated against a CLOSED vocabulary
// (validateProposal) and staged as a chip; NOTHING executes until the user hits
// Confirm. On confirm, executeAction runs the action client-side exactly as a
// button click would — an in-app navigation through the HOST's own router, a gated
// FHIR write via the user's own client, or savePrefs — so it can never do more than
// the user could do by hand.
//
// WHERE THIS LIVES, AND WHY (P2 stage-7 lift) ─────────────────────────────────
// The brain — the system prompt, the CLOSED action enum, validateProposal and
// executeAction — is PLATFORM-FREE and lives here so the web SPA and the Expo
// tablet run the SAME §2 security boundary rather than two copies of it. Two
// things in the old browser file were genuinely DOM-bound, and ONLY those two:
//   • the SPA navigation (opd-nav.js navTo) — react-router on web, expo-router
//     on the tablet;
//   • the cross-surface replay stash (window.sessionStorage) — sessionStorage on
//     web, the app's own transient store on the tablet.
// They are now INJECTED per call through `ctx.navigate` / `ctx.stashReplay` (see
// executeAction below). Neither has a default: an unregistered host FAILS LOUD
// rather than silently swallowing a navigation (AGENTS.md §1.3). The web binds
// them in its thin wrapper at opd-app/src/lib/global-agent.js, so every web
// consumer and test path is unchanged.
//
// FAIL LOUD: if `$ai` is not enabled on the project (feature 'ai' + OPENAI_API_KEY
// secret missing), the error surfaces to the caller, which shows the same honest
// message Spaces does. No Express fallback, no silent default.
import { runAgentLoop } from './medplum-ai.js';
import { loadPrefs, savePrefs } from './medplum-settings.js';
import { authHeader, apiUrl } from './client-port.js';
import { bookAppointment } from './medplum-booking.js';
import { startThreadInMedplum } from './medplum-messaging.js';

// Same-origin Express API helper — the EXACT shape ConsultCompact's api() uses
// (same base path, same Medplum bearer). apply_protocol must go through the SAME
// gated door as the consult picker's button click: POST /api/v1/opd/care-templates/
// apply carries the full apply semantics (CarePlan + RequestGroup + draft orders +
// review Tasks — what the boards/consult UI expect) AND the server-side department-
// ownership gate (authorizeApply: own-dept or hospital-wide only, fail-closed).
// Medplum's native PlanDefinition/$apply has NEITHER (bare CarePlan, no dept gate),
// so it is deliberately NOT used here.
async function opdApi(path, opts = {}) {
  const r = await fetch(apiUrl(`/api/v1/opd${path}`), { headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...authHeader() }, ...opts });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(j.message || j.error || `${r.status}`), { status: r.status });
  return j;
}

// Model: same as Spaces/command-agent — resolved by runAgentLoop from the
// deployment config (ai-model.js); the project's OPENAI_API_KEY/LLM_BASE_URL
// serves whatever backs `$ai`.
const MAX_AGENT_ITERATIONS = 8;

const cleanStr = (v) => (v === null || v === undefined ? '' : String(v).trim());

// The one canonical patient MRN identifier system (matches spaces-agent.js).
const MRN_SYSTEM = 'https://healther.io/opd/mrn';
const MRN_RE = /^UH-[A-Za-z0-9-]+$/;

// ── Navigation targets: the app-wide route table, filtered by role ────────────
// Mirrors App.jsx SCREENS + the sidebar's role rules (staff-shell.jsx) rather than
// re-deriving them — the model may only navigate to a target the user's role can
// actually open. `world` tags the OPD vs IPD side so we describe the right set.
// Keep these paths in sync with App.jsx SCREENS (static, no params).
const NAV_TARGETS = [
  // OPD world
  { key: 'queue', label: 'Consult queue (today)', path: '/queue', roles: ['doctor', 'owner', 'admin'], world: 'opd' },
  { key: 'reception', label: 'Reception / front desk', path: '/reception', roles: ['receptionist', 'reception', 'owner', 'admin'], world: 'opd' },
  { key: 'patients', label: 'Patients list', path: '/patients', roles: ['doctor', 'owner', 'admin', 'receptionist', 'reception'], world: 'opd' },
  { key: 'reports', label: 'Reports', path: '/reports', roles: ['doctor', 'owner', 'admin'], world: 'opd' },
  { key: 'schedule', label: 'Schedule', path: '/schedule', roles: ['doctor', 'owner', 'admin', 'receptionist', 'reception'], world: 'opd' },
  { key: 'tasks', label: 'My tasks / worklist', path: '/tasks', roles: null, world: 'opd' },
  { key: 'messaging', label: 'Messaging', path: '/messaging', roles: null, world: 'opd' },
  { key: 'spaces', label: 'Spaces (AI over the record)', path: '/spaces', roles: null, world: 'opd' },
  { key: 'automations', label: 'Automations / scheduled jobs', path: '/automations', roles: ['doctor', 'owner', 'admin'], world: 'opd' },
  { key: 'settings', label: 'Settings', path: '/settings', roles: null, world: 'opd' },
  { key: 'kiosk-setup', label: 'Kiosk setup', path: '/kiosk-setup', roles: null, world: 'opd' },
  { key: 'token-display', label: 'Waiting-room token display', path: '/token-display', roles: ['receptionist', 'reception', 'owner', 'admin'], world: 'opd' },
  // IPD / ward world
  { key: 'nurse', label: 'Nurse station (today)', path: '/nurse', roles: ['nurse', 'owner', 'admin'], world: 'ipd' },
  { key: 'ward', label: 'Ward board', path: '/ward', roles: ['nurse', 'doctor', 'owner', 'admin'], world: 'ipd' },
  { key: 'ward-worklist', label: 'Ward worklist (due tasks)', path: '/ward/worklist', roles: ['nurse', 'doctor', 'owner', 'admin'], world: 'ipd' },
  { key: 'ward-patients', label: 'Ward patients (in-patients)', path: '/ward/patients', roles: ['nurse', 'doctor', 'owner', 'admin'], world: 'ipd' },
  { key: 'ward-rounds', label: 'Ward rounds', path: '/ward/rounds', roles: ['nurse', 'doctor', 'owner', 'admin'], world: 'ipd' },
  { key: 'ward-templates', label: 'Ward templates', path: '/ward/templates', roles: ['nurse', 'owner', 'admin'], world: 'ipd' },
  { key: 'emergency', label: 'Emergency department board', path: '/emergency', roles: ['doctor', 'nurse', 'receptionist', 'reception', 'owner', 'admin'], world: 'emergency' },
  // Admin world
  { key: 'admin-staff', label: 'Admin — staff', path: '/admin/staff', roles: ['owner', 'admin'], world: 'admin' },
  { key: 'admin-settings', label: 'Admin — settings', path: '/admin/settings', roles: ['owner', 'admin'], world: 'admin' },
  { key: 'admin-analytics', label: 'Admin — analytics', path: '/admin/analytics', roles: ['owner', 'admin'], world: 'admin' },
  { key: 'admin-outbox', label: 'Admin — notification outbox', path: '/admin/outbox', roles: ['owner', 'admin'], world: 'admin' },
  { key: 'admin-attendance', label: 'Admin — attendance', path: '/admin/attendance', roles: ['owner', 'admin', 'doctor'], requireHODForDoctor: true, world: 'admin' },
];

// The targets a given role may open (roles:null ⇒ any logged-in staff).
function navTargetsForRole(roleOrUser) {
  const user = roleOrUser && typeof roleOrUser === 'object' ? roleOrUser : { role: roleOrUser };
  const r = String(user.role || '').toLowerCase();
  return NAV_TARGETS.filter((t) => (!t.roles || t.roles.includes(r))
    && !(t.requireHODForDoctor && r === 'doctor' && !user.isHOD));
}
// Look one up by key OR by exact path (the model may echo either).
function findNavTarget(roleOrUser, keyOrPath) {
  const q = cleanStr(keyOrPath).toLowerCase();
  return navTargetsForRole(roleOrUser).find((t) => t.key === q || t.path.toLowerCase() === q) || null;
}

const ACTION_ROLES = {
  open_patient: ['doctor', 'nurse', 'owner', 'admin', 'receptionist', 'reception'],
  apply_protocol: ['doctor', 'owner', 'admin'],
  send_message: ['doctor', 'nurse', 'owner', 'admin', 'receptionist', 'reception'],
  book_appointment: ['doctor', 'owner', 'admin', 'receptionist', 'reception'],
  star_favorite: ['doctor', 'nurse', 'owner', 'admin'],
  attach_photo: ['doctor', 'nurse', 'owner', 'admin'],
};

function actionAllowedForUser(type, roleOrUser) {
  const r = String((roleOrUser && typeof roleOrUser === 'object' ? roleOrUser.role : roleOrUser) || '').toLowerCase();
  return !ACTION_ROLES[type] || ACTION_ROLES[type].includes(r);
}

// ══════════════════════════════════════════════════════════════════════════════
// TOOL SCHEMAS — two tools: read (fhir_request GET) + propose (closed vocabulary).
// ══════════════════════════════════════════════════════════════════════════════
const FHIR_TOOL = {
  type: 'function',
  function: {
    name: 'fhir_request',
    description:
      'READ the FHIR record (GET only) to ground an answer or resolve an id before proposing an action. ' +
      "Use plain 'Type?param=value' searches or 'Type/<id>' reads, e.g. 'Patient?name=asha&_count=5', " +
      "'Task?owner=Practitioner/<me>&status=requested,ready,in-progress', 'Appointment?date=ge2026-07-08', " +
      "'PlanDefinition?status=active', 'Encounter?patient=<id>&_sort=-date'. IDs vs MRNs: 'Patient/<id>' needs a " +
      "UUID; a 'UH-…' token is an MRN identifier — GET 'Patient?identifier=<mrn>' first, then use the returned id. " +
      'You cannot WRITE with this tool — every create/update/delete/navigation MUST go through propose_actions.',
    parameters: {
      type: 'object',
      additionalProperties: false,
      properties: {
        method: { type: 'string', enum: ['GET'], description: 'Always GET — writes are blocked here.' },
        path: { type: 'string', description: "FHIR path, e.g. 'Task?owner=Practitioner/123' or 'Patient/123'." },
      },
      required: ['method', 'path'],
    },
  },
};

// The CLOSED app-wide action vocabulary the model may stage. Every write is a
// thing the user could already do by clicking a button; the frontend executes the
// kept ones through the SAME run-as-user path a click would.
const PROPOSE_TOOL = {
  type: 'function',
  function: {
    name: 'propose_actions',
    description:
      'Stage one or more actions for the user to review and confirm. NOTHING runs until they confirm — each is a ' +
      'checkbox they can keep or drop. Use this for EVERY change or navigation. The action `type` MUST be one of the ' +
      'closed vocabulary below; anything else is rejected. Give each a clear one-line `description`.\n' +
      '  • navigate_to      — go to a screen. { navKey } = a navigation target key from the list in the system prompt.\n' +
      '  • open_patient     — open a patient. { patientId (UUID), patientName?, mode?: "chart"|"consult" } (default chart).\n' +
      '  • apply_protocol   — apply a care template / protocol to a patient\'s ACTIVE visit. { planUrl (the PlanDefinition\'s canonical `url`, NOT its id), patientId (UUID), encounterId? }. Read the PlanDefinition first to get its `url`; a protocol with no canonical url cannot be applied.\n' +
      '  • create_task      — create a work Task. { title, patientId?, ownerId?, priority?: routine|urgent|asap|stat, dueDate?: ISO, note? }.\n' +
      '  • send_message     — add an in-app Communication thread/message for a patient. { patientId (UUID), text }.\n' +
      '  • book_appointment — create the canonical Appointment + Encounter visit. { patientId (UUID), practitionerId?, departmentId?, departmentName?, start: ISO, end?: ISO, reason? }. Reception must resolve and provide BOTH practitionerId and departmentId; never assign the receptionist as clinician.\n' +
      '  • star_favorite    — save a favourite medicine/test to the user\'s prefs. { kind: "medication"|"test", value (string) }.\n' +
      '  • attach_photo     — attach the photo the user has ALREADY staged (paperclip) to a patient\'s record. { patientId (UUID), patientName?, note? }. Only propose this when the user has attached a photo AND you have resolved which patient; the staged image bytes are supplied by the app, never by you.',
    parameters: {
      type: 'object',
      additionalProperties: false,
      properties: {
        actions: {
          type: 'array',
          description: 'The actions to stage for confirmation (one per discrete change).',
          items: {
            type: 'object',
            additionalProperties: true,
            properties: {
              type: { type: 'string', enum: ['navigate_to', 'open_patient', 'apply_protocol', 'create_task', 'send_message', 'book_appointment', 'star_favorite', 'attach_photo'] },
              description: { type: 'string', description: 'One-line plain description shown to the user.' },
            },
            required: ['type', 'description'],
          },
        },
      },
      required: ['actions'],
    },
  },
};

// The base app-wide action vocabulary (the `type` enum). A screen may CONTRIBUTE
// extra actions for its own surface via a surface-action registry (surface-actions.js):
// the dock passes the registered specs into askGlobalAgent, and buildProposeTool
// widens the CLOSED enum + the tool/prompt copy to include them. This keeps the
// propose-only invariant intact — the model still may only emit a `type` from a
// fixed, per-call-known set; each surface action is validated by its own validate()
// and executed by its own execute() through the SAME confirm→run-as-user path a
// built-in action uses. It can never widen what the user could do by hand.
const BASE_ACTION_ENUM = ['navigate_to', 'open_patient', 'apply_protocol', 'create_task', 'send_message', 'book_appointment', 'star_favorite', 'attach_photo'];

// The built-in app-wide actions expressed as DATA — one row per action with a plain
// describe, the parameter shape, and what the action genuinely NEEDS to run. The
// capability catalog and the honesty rules in the system prompt render from THIS
// (plus the per-surface specs surface-actions.js exports) rather than from prose
// scattered through the prompt — so the agent always sees a complete, machine-clean
// list of exactly what it can do, and a newly-registered surface action (e.g.
// set_duty) appears in the catalog automatically without touching this file.
const BUILTIN_ACTIONS = [
  { name: 'navigate_to', describe: 'go to another screen in the app', params: 'navKey (a target from the NAVIGATION list), thenAsk? (a request to run on arrival)', needs: 'a navigation target your role can open' },
  { name: 'open_patient', describe: 'open a patient in their chart (or the consult room)', params: 'patientId (UUID), patientName?, mode?: chart|consult', needs: 'a patient — resolve a name/MRN to a UUID with fhir_request first' },
  { name: 'apply_protocol', describe: "apply a care template / protocol to a patient's active visit", params: 'planUrl (the PlanDefinition canonical url, NOT its id), patientId (UUID), encounterId?', needs: 'a patient with an ACTIVE visit, and a protocol in your own department or hospital-wide' },
  { name: 'create_task', describe: 'create a work Task / reminder', params: 'title (real + imperative), patientId?, ownerId?, priority?: routine|urgent|asap|stat, dueDate?: ISO, note?', needs: 'a real imperative title — never a generic "task"/"note" placeholder' },
  { name: 'send_message', describe: 'add a visible in-app Communication thread/message for a patient (does not claim SMS/WhatsApp delivery)', params: 'patientId (UUID), text', needs: 'a patient + the message text' },
  { name: 'book_appointment', describe: 'create the canonical Appointment and linked Encounter visit', params: 'patientId (UUID), start: ISO, end?, practitionerId?, departmentId?, departmentName?, reason?', needs: 'a patient + a start time; reception must resolve an explicit clinician and clinical department' },
  { name: 'star_favorite', describe: 'save a favourite medicine or test to your own prefs', params: 'kind: medication|test, value', needs: 'nothing else' },
  { name: 'attach_photo', describe: 'attach the photo you have ALREADY staged (paperclip) to a patient', params: 'patientId (UUID), patientName?, note?', needs: 'a photo staged via the paperclip AND a resolved patient' },
];

// Render the unified capability catalog for ONE turn: the built-in actions plus the
// screen's stageable surface actions (local + cross-surface "direct"), each with its
// params and, for built-ins, what it needs. Rendered from data so a surface action
// that surface-actions.js adds later shows up here with no change to this function.
function renderCapabilityCatalog(extraActions = [], roleOrUser = null) {
  const extra = (Array.isArray(extraActions) ? extraActions : []).filter((s) => s && s.name);
  const rows = [
    ...BUILTIN_ACTIONS.filter((a) => !roleOrUser || actionAllowedForUser(a.name, roleOrUser)).map((a) => ({ ...a, remote: false })),
    ...extra.map((s) => ({ name: s.name, describe: s.describe || '', params: s.params || '', needs: null, remote: !!s.remote, nativeLabel: s.nativeLabel })),
  ];
  return rows.map((r) => {
    const tail = [
      r.params ? `{ ${r.params} }` : '',
      r.needs ? `— needs ${r.needs}` : '',
      r.remote ? `(runs from here; native to ${r.nativeLabel || 'another screen'})` : '',
    ].filter(Boolean).join(' ');
    return `  • ${r.name} — ${r.describe}${tail ? ` ${tail}` : ''}`;
  }).join('\n');
}

// Build the propose_actions tool for a given set of extra surface actions. With an
// empty set this is byte-identical to the base PROPOSE_TOOL.
function buildProposeTool(surfaceActions = [], roleOrUser = null) {
  const extra = (Array.isArray(surfaceActions) ? surfaceActions : []).filter((s) => s && s.name);
  if (!extra.length && !roleOrUser) return PROPOSE_TOOL;
  const allowedBase = BASE_ACTION_ENUM.filter((name) => !roleOrUser || actionAllowedForUser(name, roleOrUser));
  const extraLines = extra
    .map((s) => `  • ${s.name} — ${s.describe || ''}${s.params ? ` { ${s.params} }` : ''}`)
    .join('\n');
  const baseParams = PROPOSE_TOOL.function.parameters;
  const baseItems = baseParams.properties.actions.items;
  return {
    type: 'function',
    function: {
      ...PROPOSE_TOOL.function,
      description: 'Stage one or more actions for the user to review and confirm. NOTHING runs until they confirm. '
        + 'Use only an action type in this tool schema; unknown or role-forbidden actions are rejected.'
        + (extraLines ? `\nADDITIONAL actions available on this screen:\n${extraLines}` : ''),
      parameters: {
        ...baseParams,
        properties: {
          ...baseParams.properties,
          actions: {
            ...baseParams.properties.actions,
            items: {
              ...baseItems,
              properties: {
                ...baseItems.properties,
                type: { type: 'string', enum: [...allowedBase, ...extra.map((s) => s.name)] },
              },
            },
          },
        },
      },
    },
  };
}

// The tool set for a given surface (default: app-wide only).
function buildTools(surfaceActions = [], roleOrUser = null) { return [FHIR_TOOL, buildProposeTool(surfaceActions, roleOrUser)]; }

const TOOLS = [FHIR_TOOL, PROPOSE_TOOL];

// ── fhir_request path normalisation (GET-only; ported from spaces-agent.js) ────
function normaliseFhirPath(rawPath) {
  let p = cleanStr(rawPath).replace(/^\/+/, '').replace(/^fhir\/R4\//i, '').replace(/^R4\//i, '');
  if (!p) throw new Error('fhir_request: empty path');
  if (/(^|\/)_search(\b|\?|\/)/.test(p)) {
    throw new Error(`fhir_request: malformed path "${p}" — drop the "_search" artifact and use a plain "Type?param=value" query.`);
  }
  const qIdx = p.indexOf('?');
  let pathPart = qIdx >= 0 ? p.slice(0, qIdx) : p;
  let queryPart = qIdx >= 0 ? p.slice(qIdx + 1) : '';
  const segs = pathPart.split('/').filter(Boolean);
  // Patient/UH-… (an MRN, not an id) → identifier search.
  if (segs.length === 2 && segs[0] === 'Patient' && MRN_RE.test(segs[1])) {
    const mrn = segs[1];
    segs.length = 1;
    const idQ = `identifier=${encodeURIComponent(MRN_SYSTEM)}|${encodeURIComponent(mrn)}`;
    queryPart = queryPart ? `${idQ}&${queryPart}` : idQ;
  }
  // Collection reads: ask for an accurate total so counts are truthful.
  if (segs.length === 1) {
    const params = new URLSearchParams(queryPart);
    if (!params.has('_total') && !params.has('_summary')) params.set('_total', 'accurate');
    queryPart = params.toString();
  }
  return { segs, query: queryPart };
}

async function runFhirGet(medplum, path) {
  const { segs, query } = normaliseFhirPath(path);
  const url = medplum.fhirUrl(...segs);
  if (query) url.search = query;
  return medplum.get(url, { cache: 'no-cache' });
}

// Trim a FHIR result down for the model (drop narrative/meta; cap Bundle pages).
function compactForModel(result) {
  const strip = (r) => {
    if (!r || typeof r !== 'object') return r;
    const { text, meta, contained, ...rest } = r;
    return rest;
  };
  if (result && result.resourceType === 'Bundle') {
    const all = result.entry || [];
    const entry = all.slice(0, 20).map((e) => strip(e.resource));
    return Number.isFinite(result.total)
      ? { resourceType: 'Bundle', total: result.total, shown: entry.length, entry }
      : { resourceType: 'Bundle', total: null, shown: entry.length, truncated: all.length > entry.length, entry };
  }
  return strip(result);
}

function forbiddenMsg(e) {
  const forbidden = e?.status === 403 || e?.outcome?.issue?.some?.((x) => x.code === 'forbidden')
    || /forbidden|not allowed|access policy|unauthorized/i.test(e?.message || '');
  return forbidden
    ? 'access denied — your Medplum access policy forbids that. Tell the user plainly they lack access.'
    : (e && e.message) || 'read failed';
}

// ══════════════════════════════════════════════════════════════════════════════
// VALIDATION — a proposed action is accepted ONLY if it is in the closed vocab AND
// has the fields its executor needs. Mirrors command-agent.js validateAction: an
// unknown/ill-formed action is dropped (never staged), so nothing invalid reaches
// the confirm UI or the executor.
// ══════════════════════════════════════════════════════════════════════════════
const ACTION_TYPES = new Set(['navigate_to', 'open_patient', 'apply_protocol', 'create_task', 'send_message', 'book_appointment', 'star_favorite', 'attach_photo']);
const COMMAND_ACTION_IDENTIFIER_SYSTEM = 'https://healther.io/opd/command-action';

function confirmedActionKey(action) {
  const raw = cleanStr(action && action.id).toLowerCase();
  if (!raw) throw new Error('confirmed command action is missing its retry key');
  const safe = raw.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48);
  if (!safe) throw new Error('confirmed command action has an invalid retry key');
  return `agent-${cleanStr(action.type).replace(/_/g, '-')}-${safe}`;
}

function newActionSeed() {
  const randomUUID = globalThis.crypto?.randomUUID;
  if (typeof randomUUID !== 'function') {
    throw new Error('Secure action identifiers require Web Crypto randomUUID().');
  }
  return randomUUID.call(globalThis.crypto);
}

function idFromTransactionEntry(entry, resourceType) {
  const direct = entry && entry.resource && entry.resource.id;
  if (direct) return direct;
  const match = new RegExp(`${resourceType}/([^/]+)`).exec(String(entry && entry.response && entry.response.location || ''));
  return match ? match[1] : null;
}

function validateProposal(a, roleOrUser, surfaceActions = []) {
  if (!a || typeof a !== 'object') return null;
  const type = cleanStr(a.type);
  const description = cleanStr(a.description) || type;
  // A surface-registered action (settings/favourites/dose-log): validate it with
  // its OWN validate() — it returns the normalised extra fields (never a `type`/
  // `description`) or null to drop. Kept separate from the built-in switch so the
  // app-wide vocabulary and the per-surface vocabulary each own their validation.
  if (!ACTION_TYPES.has(type)) {
    const spec = (Array.isArray(surfaceActions) ? surfaceActions : []).find((s) => s && s.name === type);
    if (!spec || typeof spec.validate !== 'function') return null;
    const extra = spec.validate(a);
    if (!extra || typeof extra !== 'object') return null;
    return { type, description, ...extra };
  }
  const out = { type, description };
  const callerRole = String((roleOrUser && typeof roleOrUser === 'object' ? roleOrUser.role : roleOrUser) || '').toLowerCase();
  if (!actionAllowedForUser(type, roleOrUser)) return null;
  switch (type) {
    case 'navigate_to': {
      const tgt = findNavTarget(roleOrUser, a.navKey || a.path);
      if (!tgt) return null; // unknown target OR not permitted for this role → drop
      out.navKey = tgt.key; out.path = tgt.path; out.label = tgt.label;
      // Cross-surface escalation (owner design rule): when the user asks for something
      // that must be done on ANOTHER screen, the model stages navigate_to with the
      // ORIGINAL request in `thenAsk`. On confirm we go there AND replay the request so
      // it executes on arrival (navigate-then-replay), rather than the user re-typing it.
      out.thenAsk = cleanStr(a.thenAsk) || null;
      break;
    }
    case 'open_patient': {
      if (!cleanStr(a.patientId)) return null;
      out.patientId = cleanStr(a.patientId);
      out.patientName = cleanStr(a.patientName) || null;
      out.mode = cleanStr(a.mode).toLowerCase() === 'consult' ? 'consult' : 'chart';
      break;
    }
    case 'apply_protocol': {
      // planUrl (the canonical url) is what the gated Express route consumes — a
      // bare PlanDefinition id is NOT accepted (drop; the model is told to read
      // the url first). patientId is needed to resolve the active visit.
      const planUrl = cleanStr(a.planUrl);
      if (!/^https?:\/\//.test(planUrl) || !cleanStr(a.patientId)) return null;
      out.planUrl = planUrl;
      out.patientId = cleanStr(a.patientId);
      out.encounterId = cleanStr(a.encounterId) || null;
      break;
    }
    case 'create_task': {
      const title = cleanStr(a.title);
      // A task MUST have a real, imperative title — never the generic "General task"
      // placeholder that polluted the shared worklist (owner anti-acceptance artifact,
      // 2026-07-11). Drop empty or placeholder titles so a vague utterance can never
      // silently mint a junk task; the model is told to escalate/ask instead.
      if (!title || /^(general( task)?|task|note|to-?do|reminder|todo|item)$/i.test(title)) return null;
      out.title = title;
      out.patientId = cleanStr(a.patientId) || null;
      out.ownerId = cleanStr(a.ownerId) || null;
      out.priority = ['routine', 'urgent', 'asap', 'stat'].includes(cleanStr(a.priority).toLowerCase()) ? cleanStr(a.priority).toLowerCase() : 'routine';
      out.dueDate = cleanStr(a.dueDate) || null;
      out.note = cleanStr(a.note) || null;
      break;
    }
    case 'send_message': {
      if (!cleanStr(a.patientId) || !cleanStr(a.text)) return null;
      out.patientId = cleanStr(a.patientId);
      out.text = cleanStr(a.text);
      break;
    }
    case 'book_appointment': {
      if (!cleanStr(a.patientId) || !cleanStr(a.start)) return null;
      const practitionerId = cleanStr(a.practitionerId) || null;
      const departmentId = cleanStr(a.departmentId) || null;
      if (['reception', 'receptionist'].includes(callerRole) && (!practitionerId || !departmentId)) return null;
      out.patientId = cleanStr(a.patientId);
      out.practitionerId = practitionerId;
      out.departmentId = departmentId;
      out.departmentName = cleanStr(a.departmentName) || null;
      out.start = cleanStr(a.start);
      out.end = cleanStr(a.end) || null;
      out.reason = cleanStr(a.reason) || null;
      break;
    }
    case 'star_favorite': {
      const kind = cleanStr(a.kind).toLowerCase();
      if (!['medication', 'test'].includes(kind) || !cleanStr(a.value)) return null;
      out.kind = kind; out.value = cleanStr(a.value);
      break;
    }
    case 'attach_photo': {
      // Only the target patient is the model's job; the image bytes are the
      // app-staged file (supplied via ctx at execution). No patient → drop.
      if (!cleanStr(a.patientId)) return null;
      out.patientId = cleanStr(a.patientId);
      out.patientName = cleanStr(a.patientName) || null;
      out.note = cleanStr(a.note) || null;
      break;
    }
    default:
      return null;
  }
  return out;
}

// ══════════════════════════════════════════════════════════════════════════════
// EXECUTION — run ONE confirmed action, client-side, AS THE LOGGED-IN USER. Every
// branch goes through the same run-as-user path a manual click would: an in-app
// navigation through the host's own router, a FHIR write via the user's own
// MedplumClient (RLS-scoped), the gated care-templates apply, or savePrefs. A
// failure here surfaces to the caller (fail loud).
//
// THE TWO INJECTED HOST BINDINGS (the only platform seam in this file):
//   ctx.navigate(path, action)  — REQUIRED by navigate_to and open_patient. Take
//       the app-relative path THIS app already routes ('/queue', '/patients?…',
//       '/consult/<id>/compact') and perform the host's own in-app navigation.
//       Web: opd-nav.js navTo (react-router / full reload). Tablet: expo-router.
//   ctx.stashReplay(json, meta) — REQUIRED only when a navigate_to carries a
//       `thenAsk` (navigate-then-replay). Durably hand `json` to the destination
//       screen's dock, and THROW if it could not be persisted — a silently
//       dropped replay would make the agent claim work it never carried over.
// Both fail loud when the host did not register them: a missing binding is a
// mis-wired host, never a reason to quietly not navigate (AGENTS.md §1.3).
// ══════════════════════════════════════════════════════════════════════════════
function hostNavigate(ctx, path, action) {
  if (typeof ctx.navigate !== 'function') {
    throw new Error('global-agent: this app did not register a navigate binding, so it cannot open another screen.');
  }
  return ctx.navigate(path, action);
}

export async function executeAction(medplum, action, ctx = {}) {
  const meId = ctx.meId || null;
  // A surface-registered action runs through its OWN execute() — a thin call into
  // the screen's existing write logic (savePrefs for a setting, commitDoseTick for
  // a dose log). It receives the live page context (ctx.actionContext) the dock
  // threads in. Handled before the built-in switch so surface vocab never falls
  // through to the `default: unknown action` throw.
  if (!ACTION_TYPES.has(action.type)) {
    const spec = (Array.isArray(ctx.surfaceActions) ? ctx.surfaceActions : []).find((s) => s && s.name === action.type);
    if (spec && typeof spec.execute === 'function') {
      return spec.execute(action, { medplum, meId, user: ctx.user || null, actionContext: ctx.actionContext || {}, sourceQuote: ctx.sourceQuote || '' });
    }
    throw new Error(`unknown action type ${action.type}`);
  }
  switch (action.type) {
    case 'navigate_to':
      // Cross-surface replay: if this navigation carries a `thenAsk` (the user's
      // original request that must run on the target screen), stash it so the target
      // dock replays it on mount — navigate-then-execute, not just navigate.
      if (action.thenAsk) {
        const replay = JSON.stringify({ navKey: action.navKey, path: action.path, text: action.thenAsk, at: Date.now() });
        try {
          if (typeof ctx.stashReplay !== 'function') {
            throw new Error('this app did not register a replay binding');
          }
          // The binding MUST verify its own write and throw when it could not
          // persist — this catch turns any such failure into the one honest
          // refusal, and we never navigate on a lost replay.
          await ctx.stashReplay(replay, { navKey: action.navKey, path: action.path, text: action.thenAsk });
        } catch (cause) {
          throw new Error('Could not carry this request to the destination screen. Stay here and try again.', { cause });
        }
      }
      hostNavigate(ctx, action.path, action);
      return { ok: true, note: action.thenAsk ? `Opening ${action.label || action.path} — I'll pick up "${action.thenAsk}" there.` : undefined };
    case 'open_patient':
      if (action.mode === 'consult') {
        // Resolve the patient's current open encounter, then open the consult;
        // fall back to the patients list focused on them if none is open. NOTE:
        // searchOne returns null when nothing MATCHES but THROWS on a real read
        // error (e.g. RLS-forbidden) — we do NOT swallow that (fail loud / the
        // reason must be right), so a permission failure surfaces truthfully to the
        // user instead of masquerading as "no open encounter".
        const enc = await medplum.searchOne('Encounter',
          `subject=Patient/${action.patientId}&status=in-progress,arrived,triaged&_sort=-date`);
        if (enc && enc.id) { hostNavigate(ctx, `/consult/${enc.id}/compact`, action); return { ok: true }; }
        hostNavigate(ctx, `/patients?focus=${encodeURIComponent(action.patientId)}`, action);
        return { ok: true, note: 'no open encounter — opened the patient in Patients' };
      }
      hostNavigate(ctx, `/patients?focus=${encodeURIComponent(action.patientId)}`, action);
      return { ok: true };
    case 'apply_protocol': {
      // The SAME gated door as the consult picker's Apply button: POST
      // /care-templates/apply { planUrl, encounterId }. The Express route spawns
      // the FULL artifact set (CarePlan + RequestGroup + draft ServiceRequest
      // orders + review Tasks — what the boards/consult UI expect) and enforces
      // the department-ownership gate server-side (403 protocol-not-in-your-
      // department, fail-closed). NOT Medplum's native $apply (bare CarePlan, no
      // dept gate — that would reopen the cross-department hole just closed).
      //
      // Encounter resolution — RESOLVE-OR-REFUSE (fail closed): the apply
      // semantics attach orders/tasks to a VISIT. Use the staged encounterId when
      // present; otherwise resolve the patient's ACTIVE encounter (in-progress/
      // arrived/triaged) as the user. No active visit → refuse with guidance —
      // never invent an encounter or attach the protocol to a finished visit.
      let encounterId = action.encounterId;
      if (!encounterId) {
        // searchOne → null when nothing matches, THROWS on a real read error. We let
        // that throw propagate (fail loud) rather than swallow a permission failure
        // as "no active visit" — the refusal reason the user sees must be the true one.
        const enc = await medplum.searchOne('Encounter',
          `subject=Patient/${action.patientId}&status=in-progress,arrived,triaged&_sort=-date`);
        encounterId = enc && enc.id;
      }
      if (!encounterId) {
        throw new Error('this patient has no active visit — open or start a visit first, then apply the protocol.');
      }
      const j = await opdApi('/care-templates/apply', {
        method: 'POST',
        body: JSON.stringify({ planUrl: action.planUrl, encounterId }),
      });
      if (!j || j.ok === false) throw new Error((j && j.error) || 'could not apply the protocol');
      return {
        ok: true,
        note: `applied "${j.title || action.planUrl}" — ${(j.tasks || []).length} task${(j.tasks || []).length === 1 ? '' : 's'}, ${(j.orders || []).length} order${(j.orders || []).length === 1 ? '' : 's'}`,
      };
    }
    case 'create_task': {
      const retryKey = confirmedActionKey(action);
      const body = {
        resourceType: 'Task',
        identifier: [{ system: COMMAND_ACTION_IDENTIFIER_SYSTEM, value: retryKey }],
        status: action.ownerId ? 'in-progress' : 'requested',
        intent: 'order',
        priority: action.priority,
        code: { text: action.title, coding: [{ system: 'https://healther.io/opd/task-type', code: 'general', display: action.title }] },
        ...(action.note ? { description: action.note } : {}),
        ...(action.patientId ? { for: { reference: `Patient/${action.patientId}` } } : {}),
        ...(action.ownerId ? { owner: { reference: `Practitioner/${action.ownerId}` } } : {}),
        ...(meId ? { requester: { reference: `Practitioner/${meId}` } } : {}),
        ...(action.dueDate ? { restriction: { period: { end: action.dueDate } } } : {}),
        authoredOn: new Date().toISOString(),
      };
      const response = await medplum.executeBatch({
        resourceType: 'Bundle',
        type: 'transaction',
        entry: [{
          resource: body,
          request: {
            method: 'POST',
            url: 'Task',
            ifNoneExist: `identifier=${COMMAND_ACTION_IDENTIFIER_SYSTEM}|${encodeURIComponent(retryKey)}`,
          },
        }],
      });
      const id = idFromTransactionEntry(response?.entry?.[0], 'Task');
      if (!id) throw new Error('Task transaction returned no Task id.');
      return { ok: true, id };
    }
    case 'send_message': {
      const retryKey = confirmedActionKey(action);
      const created = await startThreadInMedplum({
        topic: 'Patient conversation', patientId: action.patientId, firstMessage: action.text,
        idempotencyKey: retryKey,
      });
      return { ok: true, id: created.id, note: 'Added to the in-app patient conversation.' };
    }
    case 'book_appointment': {
      const retryKey = confirmedActionKey(action);
      const callerRole = String(ctx.user?.role || '').toLowerCase();
      const isReception = callerRole === 'reception' || callerRole === 'receptionist';
      if (isReception && (!action.practitionerId || !action.departmentId)) {
        throw new Error('Reception bookings require an explicit clinician and clinical department.');
      }
      const doctorId = action.practitionerId || (isReception ? undefined : meId) || undefined;
      const departmentId = action.departmentId || (isReception ? undefined : (ctx.user?.departmentId || ctx.user?.department)) || undefined;
      const departmentName = action.departmentName || (isReception ? undefined : (ctx.user?.departmentName || ctx.user?.department)) || undefined;
      const created = await bookAppointment({
        patientId: action.patientId,
        doctorId,
        doctorName: doctorId === meId ? (ctx.user?.displayName || ctx.user?.name) : undefined,
        departmentId,
        departmentName,
        startISO: action.start,
        endISO: action.end || undefined,
        reason: action.reason || undefined,
        idempotencyKey: retryKey,
        source: 'command-agent',
      }, { sendLink: false });
      return { ok: true, id: created.appointmentId, encounterId: created.encounterId, note: 'Appointment and visit created.' };
    }
    case 'star_favorite': {
      // savePrefs deep-merges but arrays REPLACE wholesale (deepMergePrefs) — so
      // read the current favourites, append if absent, then save the FULL list.
      // Prefs shape: favouriteMeds:[{name,…}], favouriteTests:[{name}].
      const prefs = await loadPrefs();
      const key = action.kind === 'medication' ? 'favouriteMeds' : 'favouriteTests';
      const list = Array.isArray(prefs[key]) ? prefs[key] : [];
      const exists = list.some((f) => String(f && f.name || '').toLowerCase() === action.value.toLowerCase());
      if (exists) return { ok: true, note: 'already a favourite' };
      await savePrefs({ [key]: [...list, { name: action.value }] });
      return { ok: true };
    }
    case 'attach_photo': {
      // The image bytes are the app-STAGED file (the paperclip), passed in via
      // ctx alongside the shared write helper — the model never carries bytes.
      // FAIL LOUD if no photo was staged (nothing to attach).
      if (typeof ctx.attachPhoto !== 'function' || !ctx.photoFile) {
        throw new Error('no photo is staged — attach one with the paperclip first, then confirm');
      }
      // ctx.photoUnderstanding = the context layer's vision result ({kind,
      // summary, fields, source}) — stored in the DocumentReference bag.
      const res = await ctx.attachPhoto({ file: ctx.photoFile, patientId: action.patientId, note: action.note, understanding: ctx.photoUnderstanding || null, context: { ...(ctx.photoContext || {}), patientName: action.patientName || (ctx.photoContext && ctx.photoContext.patientName) } });
      return { ok: true, id: res.documentReferenceId, note: `photo attached to ${action.patientName || 'patient'}` };
    }
    default:
      throw new Error(`unknown action type ${action.type}`);
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// SYSTEM PROMPT — who the user is, where they are, what they can do, grounded in
// the protocol digest + the user's favourites (via agent-context.js, optional).
// ══════════════════════════════════════════════════════════════════════════════
function buildSystemPrompt({ user, pageContext, contextDigest, favorites, surfaceActions = [], surfaceContext = null, escalations = [], taskLedger = [], openTasks = [], taskContextWarning = null }) {
  const role = String(user?.role || 'staff').toLowerCase();
  const targets = navTargetsForRole(user || role);
  const extraActions = (Array.isArray(surfaceActions) ? surfaceActions : []).filter((s) => s && s.name);
  const escalationList = (Array.isArray(escalations) ? escalations : []).filter((e) => e && e.name);
  const lines = [
    'You are Healther Assistant, a pervasive command-agent inside an Indian hospital app (OPD out-patient + IPD in-patient wards).',
    'You act AS the logged-in staff member — every read and every write runs under their own access (RLS the only lock). You can',
    'do anything they could do by clicking, and NOTHING they could not: their Medplum access policy bounds you.',
    '',
    'WHO you are talking to:',
    `- Name: ${user?.displayName || user?.name || 'staff member'}${user?.id ? ` (Practitioner/${user.id})` : ''}.`,
    `- Role: ${role}${user?.isHOD ? ' (Head of Department)' : ''}${user?.department ? `, department: ${user.department}` : ''}.`,
    user?.id ? `- "me"/"my"/"assign to me" → Practitioner/${user.id}.` : '',
    '',
    'WHERE they are right now:',
    `- Screen: ${pageContext?.screen || 'unknown'} (path ${pageContext?.path || '?'}), world: ${pageContext?.world || 'opd'}.`,
    pageContext?.patientId ? `- A patient is in context: Patient/${pageContext.patientId}${pageContext.patientName ? ` (${pageContext.patientName})` : ''}. Prefer it when the user says "this patient".` : '- No patient is in context on this screen.',
    pageContext?.photoStaged ? '- The user has STAGED a photo (paperclip). If they ask to attach/add "this photo" to someone, resolve that patient and propose attach_photo { patientId }.' : '',
    pageContext?.photoStaged && pageContext?.photoUnderstanding ? `- The staged photo was auto-read as: ${pageContext.photoUnderstanding}. Use this to write a clearer attach_photo note/description; do not re-invent details beyond it.` : '',
    pageContext?.encounterId ? `- An encounter is in context: Encounter/${pageContext.encounterId}. Use it for apply_protocol when relevant.` : '',
    '',
    'WHAT you can do — you READ with fhir_request (GET only), and you PROPOSE every change or navigation with propose_actions (nothing runs until the user confirms each staged action).',
    '',
    // The unified capability catalog: the built-in app-wide actions PLUS this screen's
    // stageable surface actions, rendered from data (BUILTIN_ACTIONS + surfaceActions)
    // so the model sees a complete, current list of exactly what it can do — and a new
    // surface action appears automatically. This is the single source the honesty rule
    // below reasons against ("is this in my catalog?"), so it must be complete.
    'YOUR CAPABILITY CATALOG — the ONLY things you can do this turn. Each row is an action `type` with its parameters and what it genuinely needs. Never invent a `type` outside this list:',
    renderCapabilityCatalog(extraActions, user || role),
    extraActions.length
      ? 'Prefer the screen actions above (the ones marked "runs from here", or native to this screen) over navigating away or creating a task whenever the user asks for one of them.'
      : '',
    // Page-bound actions that live on OTHER screens: the model must ESCALATE, not
    // refuse and not fake — stage navigate_to that screen carrying the original request
    // in `thenAsk` so it runs on arrival.
    ...(escalationList.length ? [
      '',
      'These live on ANOTHER screen and need that screen open. If the user asks for one, DO NOT refuse and DO NOT create a task — say "that\'s done on <screen>" and stage navigate_to that screen with the ORIGINAL request in `thenAsk` (e.g. navigate_to { navKey: "ward-worklist", thenAsk: "log paracetamol 650 for Ramesh" }); it will run when they arrive:',
      ...escalationList.map((e) => `  • ${e.name} — on ${e.surfaceLabel} (navKey ${e.navKey || '?'}): ${e.describe || ''}`),
    ] : []),
    // The live entities on the screen right now (e.g. the visible DUE worklist items),
    // so the model can resolve "log paracetamol for Ramesh" to a specific row.
    ...(surfaceContext && cleanStr(surfaceContext) ? ['', 'CURRENT SCREEN CONTEXT (the entities visible right now — resolve the user\'s target against THIS, do not invent one):', cleanStr(surfaceContext)] : []),
    // TASKS ALREADY RECORDED (owner-decided 2026-07-14): duplicate judgement is
    // the MODEL's job, grounded in what actually exists — the session ledger
    // (confirmed in this conversation) plus the patient's live open Tasks from
    // the record. More context → better judgement: a repeat in meaning gets
    // pointed at the existing task; a different timeframe/assignee/detail is a
    // genuinely new task, staged with the difference named.
    ...((taskLedger.length || openTasks.length) ? [
      '',
      'TASKS ALREADY RECORDED — judge any new task request against THIS list, not memory:',
      ...(taskLedger.length ? ['Confirmed in THIS session:', ...taskLedger.map(taskLedgerLine)] : []),
      ...(openTasks.length ? ['Already OPEN for this patient (from the record):', ...openTasks.map((t) => `${taskLedgerLine(t)}${t.status ? ` · status ${t.status}` : ''}`)] : []),
      'Duplicate rule: same action + same patient + same timeframe in MEANING → do NOT stage another create_task; reply pointing at the existing task. A different due time, assignee, priority, or detail = a genuinely NEW task: stage it and SAY what makes it different. Unsure → ask ONE short question.',
    ] : []),
    ...(taskContextWarning ? [
      '',
      `TASK CONTEXT UNAVAILABLE — ${taskContextWarning}`,
      'Do not claim that no matching Task exists. Tell the user duplicate checking against the live record is unavailable. Continue helping with unrelated requests; if they explicitly ask to create a task, you may still stage it for confirmation, but disclose that the existing-task check could not be completed.',
    ] : []),
    '',
    'NAVIGATION targets available to THIS user (use the key as navKey):',
    ...targets.map((t) => `  • ${t.key} — ${t.label} [${t.world}]`),
    '',
    'PATIENT IDENTITY IS A HARD GATE. When the user NAMES a patient (rather than one already in context above), resolve the name with fhir_request GET Patient?name=… (or ?identifier=<MRN>) FIRST. If more than one distinct patient matches, STOP — do not act — list up to 5 candidates (name · age/sex · MRN) and ASK ONCE which one. Exactly one match → proceed, and open by naming who you resolved. A patient already in context needs no lookup.',
    '',
    'HOW to act:',
    '- Navigate ("take me to the queue", "open my worklist", "go to reports"): stage navigate_to with the matching navKey from the navigation list above.',
    '- Open a patient: resolve to a UUID first, disambiguate per the identity gate, then stage open_patient (mode "consult" only if they explicitly want the consult room).',
    '- Apply a protocol: fhir_request GET PlanDefinition?status=active for its canonical `url` (no url → cannot apply, say so), resolve the patient, then stage apply_protocol { planUrl, patientId, encounterId? }. Use the in-context encounter if present; otherwise the active visit is resolved at confirm time (no active visit → the apply is refused with guidance). Only your own department\'s or hospital-wide protocols apply — a cross-department apply is refused server-side; relay that plainly.',
    '- create_task / send_message / book_appointment: resolve any patient/practitioner to a UUID first, then stage. create_task is ONLY an explicit to-do the user asks to add ("remind me to…", "make a task to…") — it is NEVER a fallback for a clinical action you cannot do here (logging a dose, recording a vital, an assessment go via the matching catalog action, or the right screen via escalation, or NOT AT ALL). Every task needs a real imperative title (never "General task"/"Note") and, if it is about a named patient, that patient\'s id — if you cannot resolve them, ASK; never create an unassigned, patient-less task. For repeat requests, apply the Duplicate rule under TASKS ALREADY RECORDED above — never stage a meaning-duplicate; a different due time/assignee/priority/detail is a genuinely new task.',
    '',
    // The owner's hard rule (2026-07-11): be genuinely capable OR genuinely honest,
    // and the reason must be RIGHT — derived from the catalog / escalations / context
    // above, never invented. This is the behaviour Spaces was hardened to; adapted to
    // this agent's actual powers (a closed action catalog + cross-surface escalation).
    'CAPABILITY HONESTY — the owner\'s hard rule. Be genuinely useful OR genuinely honest, and give the RIGHT reason (drawn from the catalog / escalations / context above — never invented):',
    '  (a) The request maps to an action in your catalog (including a "runs from here" cross-surface one) → DO it: resolve the ids with fhir_request, then stage it. Never merely DESCRIBE or acknowledge an action you could actually take — act on it.',
    '  (b) It maps to an action that lives on ANOTHER screen (the escalation list above) → do NOT refuse and do NOT fake a task. Say "that\'s done on <screen>" and stage navigate_to that screen with the original request in `thenAsk`, so it runs on arrival.',
    '  (c) It maps to an action but the context is missing something → name the ONE missing thing and act on it: needs a patient and none is in context → ASK ONCE which patient (resolve a given name first), then proceed — do not refuse; needs an active visit / a staged photo / a field you don\'t have → say exactly that one missing thing and how to supply it; needs a role or access you lack (a nav target not in your list, a cross-department protocol, an RLS-forbidden read/write) → say plainly you don\'t have access to that, and why.',
    '  (d) It maps to NO action anywhere — not here, not on another screen → say plainly "I can\'t do that from here" and, if you know it, where it belongs (record/history questions about a patient → open the patient\'s Space; reassigning or reviewing tasks → the Tasks screen). Never invent a capability, an id, or a reason to cover a gap.',
    '- Read before you write. Never invent a clinical fact, a patient, a name, a date, or an id — use only ids you actually read back from FHIR.',
    '- COUNTS: report a "how many" ONLY from a search Bundle\'s total, never by counting returned entries.',
    '- If a read or a proposed write is refused for access, say so plainly — never pretend it worked.',
    '- Keep replies short and practical. After staging, briefly say what you proposed and stop — the user confirms; you never execute.',
    `Today is ${new Date().toISOString().slice(0, 10)}.`,
  ];
  // Optional: the protocol digest + the user's favourites (agent-context.js). Both
  // degrade to omitted if that module/Basic is not present yet (parallel build).
  if (contextDigest && cleanStr(contextDigest)) {
    lines.push('', 'PROTOCOLS / CARE-TEMPLATE CONTEXT (for grounding apply_protocol and answers):', cleanStr(contextDigest));
  }
  if (Array.isArray(favorites) && favorites.length) {
    lines.push('', `YOUR FREQUENT FAVOURITES: ${favorites.slice(0, 20).map((f) => (typeof f === 'string' ? f : (f.label || f.name || f.value))).filter(Boolean).join(', ')}.`);
  }
  return lines.filter((l) => l !== undefined && l !== null).join('\n');
}

// ── The tool dispatcher for runAgentLoop ──────────────────────────────────────
//   fhir_request GET  → run as the user, feed a compacted result back.
//   fhir_request non-GET → blocked (writes go through propose_actions).
//   propose_actions   → validate against the closed vocab + stage (never execute).
function makeExecuteToolCall(medplum, user, stagedCounter, surfaceActions = []) {
  return async function executeToolCall(name, args) {
    if (name === 'propose_actions') {
      const accepted = [];
      for (const a of (Array.isArray(args.actions) ? args.actions : [])) {
        const v = validateProposal(a, user, surfaceActions);
        if (!v) continue;
        stagedCounter.n += 1;
        // Id must be unique ACROSS turns (the confirm UI merges staged sets from
        // successive asks and dedupes by id) — a per-call counter alone collides
        // turn-to-turn and silently drops the fresh proposal.
        accepted.push({ id: `act_${stagedCounter.seed}_${stagedCounter.n}`, ...v });
      }
      return {
        staged: accepted,
        step: { tool: 'propose_actions', staged: accepted.length },
        toolResult: {
          staged: accepted.length,
          dropped: (Array.isArray(args.actions) ? args.actions.length : 0) - accepted.length,
          note: 'Actions staged for the user to confirm — do NOT execute or re-stage them. Briefly summarise what you proposed, then stop. Any dropped action was outside the allowed vocabulary or missing a required field.',
        },
      };
    }
    if (name !== 'fhir_request') {
      return { toolResult: { error: `unknown tool ${name}` }, step: { tool: name, ok: false, summary: `unknown tool ${name}` } };
    }
    const method = cleanStr(args.method).toUpperCase() || 'GET';
    const path = cleanStr(args.path);
    if (method !== 'GET') {
      return {
        step: { tool: 'fhir_request', method, path, ok: false, summary: `blocked direct write ${method} ${path}` },
        toolResult: { error: 'Direct writes are blocked — stage every change with propose_actions so the user can confirm it.' },
      };
    }
    try {
      const result = await runFhirGet(medplum, path);
      return {
        step: { tool: 'fhir_request', method, path, ok: true, summary: `read ${path}` },
        toolResult: compactForModel(result),
      };
    } catch (e) {
      return {
        step: { tool: 'fhir_request', method, path, ok: false, summary: `failed to read ${path}` },
        toolResult: { error: forbiddenMsg(e) },
      };
    }
  };
}

/**
 * Ask the global agent. Runs the native `$ai` agent loop AS THE LOGGED-IN USER and
 * returns { reply, steps, proposedActions } — proposedActions are STAGED (validated,
 * closed-vocabulary), never executed here; the caller's confirm UI runs the kept
 * ones via executeAction(medplum, action, { meId }).
 *
 *   askGlobalAgent({
 *     medplum,       // the logged-in MedplumClient (the user's session)
 *     user,          // { id, name, displayName, role, department, isHOD } (deriveCurrentUser)
 *     pageContext,   // { screen, path, world, patientId?, patientName?, encounterId? }
 *     messages,      // [{ role:'user'|'assistant', content }] prior turns + the current one
 *     contextDigest, // optional string — protocol digest (agent-context.js)
 *     favorites,     // optional array — the user's favourites (agent-context.js)
 *   })
 */
/**
 * Stable exact-match signature for a create_task action — the mechanical
 * backstop of the owner's anti-duplicate rule (2026-07-14 restoration). The
 * ORIGINAL rule keyed on title+patient only, which wrongly suppressed a
 * legitimate second task with a different due date; this signature includes
 * due date, note, owner and priority, so only a true byte-for-byte repeat
 * matches. Anything
 * subtler than an exact repeat is the LLM's call, grounded by the TASKS
 * ALREADY RECORDED section the prompt carries.
 */
export function taskActionSignature(a) {
  if (!a || a.type !== 'create_task') return null;
  const norm = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const priority = norm(a.priority) || 'routine';
  return [norm(a.title), norm(a.patientId), norm(a.dueDate), norm(a.note), norm(a.ownerId), priority].join('|');
}

// One grounding line per recorded/open task, shared by both prompt sections.
function taskLedgerLine(t) {
  const when = t.at ? new Date(t.at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : null;
  const reference = t.reference || (t.taskId ? `Task/${t.taskId}` : null);
  const owner = t.ownerReference || (t.ownerId ? `Practitioner/${t.ownerId}` : null);
  return `  • "${t.title}"${reference ? ` [${reference}]` : ''}${t.patientName ? ` — ${t.patientName}` : ''}${t.due ? ` · due ${t.due}` : ''}${owner ? ` · owner ${owner}` : ' · unassigned'}${t.priority ? ` · priority ${t.priority}` : ''}${t.note ? ` · ${t.note}` : ''}${when ? ` · recorded ${when}` : ''}`;
}

export async function askGlobalAgent({ medplum, user, pageContext = {}, messages = [], contextDigest = null, favorites = null, surfaceActions = [], surfaceContext = null, escalations = [], taskLedger = [] }) {
  if (!medplum) throw new Error('askGlobalAgent: a logged-in MedplumClient is required');
  const role = String(user?.role || 'staff').toLowerCase();
  // This seed becomes a project-wide conditional-create identifier after the
  // user confirms an action. A short Math.random token can collide across
  // clinicians/sessions and incorrectly collapse a real write as a retry.
  // Require the platform's cryptographic UUID generator and fail loud if the
  // runtime does not provide it.
  const stagedCounter = { n: 0, seed: newActionSeed() };
  const priorTurns = (Array.isArray(messages) ? messages.slice(-8) : [])
    .filter((m) => m && m.role && m.content)
    .map((m) => ({ role: m.role === 'assistant' ? 'assistant' : 'user', content: String(m.content).slice(0, 4000) }));

  // TASKS ALREADY RECORDED grounding (owner-decided 2026-07-14): the model can
  // only judge "is this a duplicate or a genuinely new task?" if it can SEE
  // what already exists — the session ledger (tasks confirmed in this route
  // session, passed by the dock) plus the patient's live open Tasks from the
  // record (so yesterday's task counts too, not just this session's). The
  // fetch is RLS-scoped (the caller's own token) and advisory: a role whose
  // policy denies Task reads must still be able to ask unrelated questions.
  // A failed read is therefore carried into both the model prompt and the UI as
  // an explicit warning; it is never silently re-labelled as "no open tasks".
  let openTasks = [];
  let taskContextWarning = null;
  if (pageContext.patientId) {
    try {
      const rows = await medplum.searchResources(
        'Task',
        `patient=Patient/${encodeURIComponent(pageContext.patientId)}&_count=20&_sort=-authored`,
      );
      openTasks = (rows || [])
        .filter((t) => t && !/^(completed|cancelled|rejected|failed|entered-in-error)$/.test(String(t.status || '')))
        .slice(0, 10)
        .map((t) => ({
          taskId: t.id || null,
          reference: t.id ? `Task/${t.id}` : null,
          title: (t.code && (t.code.text || (t.code.coding && t.code.coding[0] && t.code.coding[0].display))) || 'Task',
          patientName: pageContext.patientName || null,
          due: (t.restriction && t.restriction.period && t.restriction.period.end) || null,
          ownerReference: (t.owner && t.owner.reference) || null,
          ownerId: String((t.owner && t.owner.reference) || '').replace(/^Practitioner\//, '') || null,
          priority: t.priority || 'routine',
          note: t.description || null,
          status: t.status || null,
        }));
    } catch (error) {
      taskContextWarning = `Could not read this patient's open Tasks: ${forbiddenMsg(error)}`;
    }
  }

  const { reply, steps, proposedActions } = await runAgentLoop({
    medplum,
    systemPrompt: buildSystemPrompt({ user, pageContext, contextDigest, favorites, surfaceActions, surfaceContext, escalations, taskLedger, openTasks, taskContextWarning }),
    messages: priorTurns,
    tools: buildTools(surfaceActions, user || { role }),
    executeToolCall: makeExecuteToolCall(medplum, user || { role }, stagedCounter, surfaceActions),
    maxIterations: MAX_AGENT_ITERATIONS,
  });

  return { ok: true, reply, steps, proposedActions: proposedActions || [], runAsUser: true, taskContextWarning };
}

// Test surface for node harnesses (mirrors spaces-agent's __spacesAgentTest).
export const __globalAgentTest = { normaliseFhirPath, compactForModel, validateProposal, buildSystemPrompt, navTargetsForRole, findNavTarget, actionAllowedForUser, newActionSeed, NAV_TARGETS, TOOLS, buildProposeTool, buildTools, BASE_ACTION_ENUM, BUILTIN_ACTIONS, renderCapabilityCatalog };
